<template>
  <BasePageFull :body-scroll="false">
    <!--#region header -->
    <template #title>
      <div class="flex space-x-3">
        <font-awesome-icon :icon="cIcon" size="2x" />
        <span class="text-2xl font-bold capitalize-first">{{ cTitle }}</span>
      </div>
    </template>

    <template #cta>
      <BaseButton
        color="primary"
        icon="plus"
        @click="this.$router.push({ name: 'work_form_new_route' })"
        :title="$t('button.add_new_m', { var: $t('global.work') })"
      />
    </template>
    <!--#endregion -->

    <!--#region settings -->
    <template #body>
      <BaseContentTable
        v-if="metadata?.columns"
        :columns="metadata.columns"
        :modelList="cWorkList"
        :loading="loading"
        :search="true"
        @rowClick="redirectToModelPage"
        @refreshClick="fetchCurrentList"
        @deleteModel="deleteModelById"
        :isHoverable="true"
        :isResponsive="true"
        scrollable
      >
        <template #toolbar>
          <TheWorkIndexSettingsComponent
            @filterChange="setDateFilter"
            @otherFilterChange="setOtherFilter"
            @customerFilter="customerFilterChange"
            @resetFilter="resetFilter"
          />
        </template>
      </BaseContentTable>
    </template>
    <!--#endregion -->
  </BasePageFull>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

import TheWorkIndexSettingsComponent from './components/TheWorkIndexSettingsComponent'

export default {
  name: 'TheWorkIndexPage',
  components: { TheWorkIndexSettingsComponent },
  data() {
    return {
      filterKind: [],
      filterKindStatus: [],
      customerFilter: [],
      filterValue: null,
      loading: false,
      abortController: null
    }
  },
  computed: {
    ...mapGetters({
      workList: 'work/getWorkIndex',
      metadata: 'work/getWorkMetadata'
    }),
    cTitle() {
      return this.$route.name === 'work_archived_route'
        ? this.$t('navigation.work_archived')
        : this.$t('navigation.work_progress')
    },
    cIcon() {
      return this.$route.name === 'work_archived_route'
        ? ['fal', 'archive']
        : ['fal', 'folder']
    },
    cWorkList() {
      let tempModels = this.workList
      if (this.filterKind.includes('today')) {
        tempModels = tempModels.filter(
          (work) =>
            work?.cremation?.value != null &&
            moment(work?.cremation?.value).format('DD/MM/YYYY') ===
              moment().format('DD/MM/YYYY')
        )
      }
      if (this.filterKind.includes('tomorrow')) {
        tempModels = tempModels.filter(
          (work) =>
            work?.cremation?.value != null &&
            moment(work?.cremation?.value).format('DD/MM/YYYY') ===
              moment().add(1, 'd').format('DD/MM/YYYY')
        )
      }
      if (this.filterKind.includes('day_after_tomorrow')) {
        tempModels = tempModels.filter(
          (work) =>
            work?.cremation?.value != null &&
            moment(work?.cremation?.value).format('DD/MM/YYYY') ===
              moment().add(2, 'd').format('DD/MM/YYYY')
        )
      }
      return tempModels
    }
  },
  methods: {
    ...mapActions({
      fetchWorkIndexMetadata: 'work/fetchWorkIndexMetadata',
      fetchWorkList: 'work/fetchWorkList',
      deleteWorkById: 'work/deleteWorkById',
      undoDeleteWorkById: 'work/undoDeleteWorkById'
    }),

    setDateFilter(filter) {
      if (filter.selected) {
        this.filterKind.push(filter.value)
      } else {
        this.filterKind = this.filterKind.filter((value) => {
          return value !== filter.value
        })
      }
    },
    // Status and customer filters are applied by the API (status[],
    // customer_id[]); the cremation date shortcuts stay client-side.
    setOtherFilter(status) {
      this.filterKindStatus = [...status]
      this.fetchCurrentList()
    },
    customerFilterChange(filters) {
      this.customerFilter = (filters?.customer ?? []).map((c) => c.id)
      this.fetchCurrentList()
    },
    resetFilter() {
      this.filterKind = []
      this.filterKindStatus = []
      this.customerFilter = []
      this.fetchCurrentList()
    },
    /**
     * The list of the current route (in progress or archived). A request
     * still running for the previous list or filters is aborted.
     */
    fetchCurrentList() {
      const filters = {}
      if (this.$route.name === 'work_archived_route') {
        filters.status = ['ARC']
      } else if (this.filterKindStatus.length) {
        filters.status = this.filterKindStatus
      }
      if (this.customerFilter.length) {
        filters.customer_id = this.customerFilter
      }
      this.abortController?.abort()
      const controller = new AbortController()
      this.abortController = controller
      this.loading = true
      return this.fetchWorkList({ filters, signal: controller.signal }).finally(
        () => {
          if (this.abortController === controller) this.loading = false
        }
      )
    },
    redirectToForm() {
      this.$router.push({ name: 'work_create_route' })
    },
    redirectToModelPage(id) {
      this.$router.push({ name: 'work_form_edit_route', params: { id: id } })
    },
    deleteModelById(work_id) {
      this.deleteWorkById({ work_id: work_id }).then(() => {
        this.fetchCurrentList()
        this.$h.toastAction(
          'delete',
          this.$t('toasts.deleted_m', { var: this.$t('attributes.work') }),
          null,
          [
            {
              title: this.$t('button.cancel'),
              class: 'text-red-700',
              action: () => this.undoDeleteWork(work_id)
            }
          ]
        )
      })
    },
    undoDeleteWork(work_id) {
      this.undoDeleteWorkById({ work_id: work_id }).then(() => {
        this.fetchCurrentList()
        this.$h.toastSuccess(
          this.$t('toasts.reseted_m', { var: this.$t('attributes.work') })
        )
      })
    }
  },
  watch: {
    dArrayFilter: {
      handler(newValue) {},
      deep: true
    },
    '$route.name'(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.fetchCurrentList()
      }
    }
  },
  beforeUnmount() {
    this.abortController?.abort()
  },
  async mounted() {
    await this.fetchWorkIndexMetadata()
    await this.fetchCurrentList()
  }
}
</script>
